import type { PhotoAPI } from '../../api/api-switcher'
import { LOAD_PHOTOS_LENGTH } from '../../utils/config'

type Photo = NonNullable<Awaited<ReturnType<typeof PhotoAPI.listPhotos>>['data']>[number]
export type PartialKey = 'photo_id' | 'added'
export type PartialPhoto<K extends PartialKey> = Photo | (
  Pick<Photo, 'added' | K> & {
    loading_key: number // キーがある時はロード中,0の時はロード済み
  }
)
export type Offset<K extends PartialKey> = K extends 'photo_id' ? {date: Date, id: string} : Date

export const findLoadTop = <K extends PartialKey>(key: K, photos: PartialPhoto<K>[], added: Date): Offset<K> | undefined => {
  const photo_index = photos.findIndex(p=> p.added === added)
  const around_photos = photos.slice(Math.max(0, photo_index - 100), photo_index + 100)
  if (
    // 前後100枚のうちロードされていないものがあり、かつ一枚もロード中でない
    around_photos.some(p=> p && 'loading_key' in p && !p.loading_key)
    && !around_photos.some(p=> p && 'loading_key' in p && p.loading_key)
  ) {
    const load_photos = photos.slice(
      Math.max(0, photo_index - (LOAD_PHOTOS_LENGTH >> 1)),
      photo_index + (LOAD_PHOTOS_LENGTH >> 1)
    )
    const load_top_photo = load_photos.find(p=> p && 'loading_key' in p)
    return (
      key === 'photo_id'
        ? {date: (load_top_photo as PartialPhoto<'photo_id'>).added, id: (load_top_photo as PartialPhoto<'photo_id'>).photo_id}
        : load_top_photo?.added
    ) as Offset<K>
  }
  else return undefined
}

// photosをnew_photosで上書きする(削除もする)
export const updatePhotos = <K extends PartialKey>(
  key: K, loading_key: number, photos: PartialPhoto<K>[], new_photos: PartialPhoto<K>[]
): PartialPhoto<K>[]=> {
  if (key === 'added') {
    const new_photos_map = new Map(new_photos.map(p=> [p.added.getTime(), p]))
    // getTimeじゃないと何故かマッチしないパターンがあったのでこちらに矯正
    return photos.map(p=> {
      const new_photo = new_photos_map.get(p.added.getTime())
      const is_loading = 'loading_key' in p && p.loading_key === loading_key
      if (is_loading) {
        p.loading_key = 0
        // アップロード指定されているのに存在しない場合は削除されているとみなす
        if (!new_photo) return undefined
      }
      // urlsがあってかつurlsのthumbnailが無い場合は存在しないとみなす
      if (new_photo && 'urls' in new_photo && new_photo.urls.thumbnail === '') return undefined
      return 'photo_id' in p ? p : (new_photo || p)
    })
      .filter(p=> p !== undefined)
  }
  else {
    const new_photos_map = new Map(new_photos.map(p=> [p[key], p]))
    return photos.map(p=> {
      const new_photo = new_photos_map.get(p[key])
      const is_loading = 'loading_key' in p && p.loading_key === loading_key
      if (is_loading) {
        p.loading_key = 0
        // アップロード指定されているのに存在しない場合は削除されているとみなす
        if (!new_photo) return undefined
      }
      // urlsがあってかつurlsのthumbnailが無い場合は存在しないとみなす
      if (new_photo && 'urls' in new_photo && new_photo.urls.thumbnail === '') return undefined
      return 'urls' in p ? p : (new_photo || p)
    })
      .filter(p=> p !== undefined)
  }
}

export const mergePhotos = <K extends PartialKey>(
  key: K, sort_desk: boolean, or_photos: PartialPhoto<K>[], ch_photos: PartialPhoto<K>[]
): PartialPhoto<K>[]=> {
  if (key === 'added') {
    const ch_photos_map = new Map(ch_photos.map(p=> [p.added.getTime(), p]))
    // getTimeじゃないと何故かマッチしないパターンがある
    const new_photos = or_photos.map(p=> {
      const added = p.added.getTime()
      const new_photo = ch_photos_map.get(added)
      ch_photos_map.delete(added) // 新しいやつだけそのまま残る
      return 'photo_id' in p ? p : (new_photo || p)
    })
    return [...new_photos, ...ch_photos_map.values()].sort(
      sort_desk ?
        (a, b)=> b.added.getTime() - a.added.getTime()
      :
        (a, b)=> a.added.getTime() - b.added.getTime()
    )
  }
  else {
    const ch_photos_map = new Map(ch_photos.map(p=> [p[key], p]))
    const new_photos = or_photos.map(p=> {
      const new_photo = ch_photos_map.get(p[key])
      ch_photos_map.delete(p[key]) // 新しいやつだけそのまま残る
      return 'urls' in p ? p : (new_photo || p)
    })
    return [...new_photos, ...ch_photos_map.values()].sort(
      sort_desk ?
        (a, b)=> b.added.getTime() - a.added.getTime()
      :
        (a, b)=> a.added.getTime() - b.added.getTime()
    )
  }
}
